import React, { createContext, useEffect, useState, useCallback } from "react";

export type Theme = "light" | "dark";

export interface ThemeProviderContextValue {
  theme: Theme;
  resolvedTheme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

export const ThemeProviderContext = createContext<ThemeProviderContextValue | undefined>(undefined);

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
  useSystemPreference?: boolean;
}

function getInitialTheme(storageKey: string, defaultTheme: Theme, useSystemPreference: boolean): Theme {
  if (typeof window !== "undefined") {
    const stored = localStorage.getItem(storageKey) as Theme | null;
    if (stored) return stored;
    if (useSystemPreference) {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      return mediaQuery.matches ? "dark" : "light";
    }
  }
  return defaultTheme;
}

export function ThemeProvider({
  children,
  defaultTheme = "light",
  storageKey = "rc-acs-theme",
  useSystemPreference = false,
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => getInitialTheme(storageKey, defaultTheme, useSystemPreference));

  const resolvedTheme = theme;

  // Listen for system preference changes when useSystemPreference is enabled
  useEffect(() => {
    if (!useSystemPreference) return;
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => {
      const stored = localStorage.getItem(storageKey);
      if (!stored) {
        setThemeState(e.matches ? "dark" : "light");
      }
    };
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, [useSystemPreference, storageKey]);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, [theme]);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    try { localStorage.setItem(storageKey, newTheme); } catch { /* incognito */ }
  }, [storageKey]);

  const toggleTheme = useCallback(() => {
    setTheme(theme === "light" ? "dark" : "light");
  }, [theme, setTheme]);

  return (
    <ThemeProviderContext.Provider value={{ theme, resolvedTheme, toggleTheme, setTheme }}>
      {children}
    </ThemeProviderContext.Provider>
  );
}
